import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const badges = [
  { label: 'Licensed Clinical Psychologist' },
  { label: 'Member, National Association of Clinical Psychologists (NACP)' },
  { label: 'Conference Speaker' },
];

export default function CredentialsAffiliationSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const badgesRef = useRef<HTMLDivElement>(null);
  const [badgesVisible, setBadgesVisible] = useState(false);

  // Intersection Observer for CSS-driven badge animations
  useEffect(() => {
    const el = badgesRef.current;
    if (!el) return;

    let disconnected = false;
    const fallbackTimer = setTimeout(() => {
      if (!disconnected) setBadgesVisible(true);
    }, 3000);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setBadgesVisible(true);
          observer.disconnect();
          disconnected = true;
          clearTimeout(fallbackTimer);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      disconnected = true;
      clearTimeout(fallbackTimer);
    };
  }, []);

  // GSAP for header
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-[#FDFAF8] py-16 md:py-28 lg:py-32"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-5xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-14">
          <p className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase mb-6">
            Credentials and Affiliations
          </p>
          <h2 className="font-heading text-xl md:text-3xl lg:text-5xl leading-[1.2] text-[#1A1A1A]">
            The foundation
            <br />
            she stands on.
          </h2>
        </div>

        {/* Credentials Badges */}
        <div
          ref={badgesRef}
          className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 md:gap-4 mb-16"
        >
          {badges.map((badge, idx) => (
            <span
              key={badge.label}
              className="w-full sm:w-auto inline-block px-5 py-2.5 border border-[#C9A84C] rounded-full font-body text-sm text-[#C9A84C] text-center sm:whitespace-nowrap"
              style={
                badgesVisible
                  ? { animation: `fadeInUp 0.8s ease-out ${idx * 0.12}s both` }
                  : { opacity: 0 }
              }
            >
              {badge.label}
            </span>
          ))}
        </div>

        {/* Gold Divider */}
        <div className="w-16 h-px bg-[#C9A84C] mx-auto" />
      </div>
    </section>
  );
}